import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Injectable, inject, signal } from '@angular/core'
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router'
import { Observable, of, throwError } from 'rxjs'
import { catchError, finalize, map, shareReplay, tap, timeout } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

export type AccountAccessMode = 'legacy-stripe' | 'database'

/** The running API owns rollout mode; a promoted frontend never bakes it in. */
@Injectable({ providedIn: 'root' })
export class RuntimeCapabilitiesService {
  readonly mode = signal<AccountAccessMode | null>(null)
  readonly loading = signal(false)
  readonly error = signal('')
  private pending?: Observable<AccountAccessMode>
  requestedNavigation: string | null = null

  get legacyStripe() { return this.mode() === 'legacy-stripe' }
  get database() { return this.mode() === 'database' }

  constructor(private http: HttpClient) {}

  load(): Observable<AccountAccessMode> {
    if (this.mode()) return of(this.mode()!)
    if (this.pending) return this.pending
    this.loading.set(true)
    this.error.set('')
    this.pending = this.http.get<unknown>(`${environment.api}/capabilities`).pipe(
      timeout({ first: 15_000 }),
      map(value => {
        const capability = value as { schemaVersion?: unknown; accountAccessMode?: unknown } | null
        if (capability?.schemaVersion !== 1 ||
            !['legacy-stripe', 'database'].includes(capability.accountAccessMode as string)) {
          throw new Error('Unsupported application capabilities')
        }
        return capability.accountAccessMode as AccountAccessMode
      }),
      catchError(error => error instanceof HttpErrorResponse && error.status === 404
        ? of('legacy-stripe' as const)
        : throwError(() => error)),
      tap(mode => this.mode.set(mode)),
      catchError(error => {
        this.error.set('We could not load account access settings. Please retry or contact support.')
        return throwError(() => error)
      }),
      finalize(() => { this.loading.set(false); this.pending = undefined }),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
    return this.pending
  }
}

/** Parent guard also covers SDK/programmatic navigation before startup resolves. */
export const capabilitiesReadyGuard: CanActivateChildFn = (route, state) => {
  if (route.routeConfig?.path === 'error/:code' && route.paramMap.get('code') === '404') return true
  const capabilities = inject(RuntimeCapabilitiesService)
  if (!capabilities.mode()) capabilities.requestedNavigation = state.url
  return capabilities.load().pipe(map(() => true), catchError(() => of(false)))
}

export const legacyStripeGuard: CanActivateFn = () => {
  const capabilities = inject(RuntimeCapabilitiesService)
  const router = inject(Router)
  return capabilities.load().pipe(
    map(mode => mode === 'legacy-stripe' ? true : router.createUrlTree(['/login'])),
    catchError(() => of(false)),
  )
}

export const databaseAccessGuard: CanActivateFn = () => {
  const capabilities = inject(RuntimeCapabilitiesService)
  const router = inject(Router)
  return capabilities.load().pipe(
    map(mode => mode === 'database' ? true : router.createUrlTree(['/admin/org-settings'])),
    catchError(() => of(false)),
  )
}
